import { PRICING_OPTION_LABELS } from '../../constants';

import type { OverrideBasis, OverrideSet, OverridesValues } from './types';

export const BASIS_OPTIONS: ReadonlyArray<{ value: OverrideBasis; label: string }> = [
  { value: 'vendor_revenue', label: 'Vendor revenue' },
  { value: 'room_nights', label: 'Room nights' },
];

const VALID_BASES = BASIS_OPTIONS.map(({ value }) => value);

// Mirrors svc-offer `pricingToolDraftValidator.ts` override tier rules: each tier's
// `to` and `comms` are required numbers, `from` (when present) must be less than `to`,
// and tiers must be ordered by increasing `to`.
export function validateOverrides(values: OverrideSet): Array<string> {
  const errors: Array<string> = [];
  if (!VALID_BASES.includes(values.basis)) {
    errors.push('Basis must be vendor_revenue or room_nights.');
  }

  let previousTo: number | undefined;
  values.tiers.forEach((tier, index) => {
    if (!tier.name?.trim()) {
      errors.push(`Tier ${index + 1}: name is required.`);
    }

    if (typeof tier.to !== 'number' || !Number.isFinite(tier.to)) {
      errors.push(`Tier ${index + 1}: "to" is required and must be a number.`);
    }

    if (typeof tier.comms !== 'number' || !Number.isFinite(tier.comms)) {
      errors.push(`Tier ${index + 1}: "comms" is required and must be a number.`);
    }

    if (tier.from !== undefined && typeof tier.to === 'number' && tier.from >= tier.to) {
      errors.push(`Tier ${index + 1}: "from" must be less than "to".`);
    }

    if (previousTo !== undefined && typeof tier.to === 'number' && tier.to <= previousTo) {
      errors.push(`Tier ${index + 1}: tiers must be ordered by increasing "to".`);
    }

    previousTo = typeof tier.to === 'number' ? tier.to : previousTo;
  });

  return errors;
}

export function defaultTierName(index: number) {
  return `Tier ${index + 1}`;
}

export function describeInvalidOverrides(overrides: OverridesValues): string | undefined {
  for (const [option, set] of Object.entries(overrides.options)) {
    const [error] = validateOverrides(set);
    if (error) {
      return `${PRICING_OPTION_LABELS[Number(option)]} overrides: ${error}`;
    }
  }
  return undefined;
}
